import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Check, Loader, Sparkles } from 'lucide-react';
import { posthog } from 'posthog-js';
import { useOnboarding } from './OnboardingContext';

interface OnboardingSurveyProps {
  onClose: () => void;
  onComplete: () => void;
}

const LoadingScreen = ({
  recordingSources,
  useCases,
}: {
  recordingSources: Set<string>;
  useCases: Set<string>;
}) => {
  const [messageIndex, setMessageIndex] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);

  const getPersonalizedMessages = () => {
    const messages = [];

    messages.push({ text: 'Analyzing your answers...', icon: Sparkles });

    if (
      recordingSources.has('Work meetings & calls') ||
      recordingSources.has('School lectures & study sessions')
    ) {
      messages.push({ text: 'Setting up meeting templates...', icon: Check });
    } else if (
      recordingSources.has('Podcasts') ||
      recordingSources.has('YouTube content')
    ) {
      messages.push({ text: 'Preparing content tools...', icon: Check });
    } else {
      messages.push({ text: 'Setting up your workspace...', icon: Check });
    }

    if (
      useCases.has('AI-generated summaries') ||
      useCases.has('Chat with my recordings')
    ) {
      messages.push({ text: 'Customizing your AI assistant...', icon: Check });
    } else if (useCases.has('Searchable transcripts')) {
      messages.push({ text: 'Optimizing search features...', icon: Check });
    } else {
      messages.push({ text: 'Personalizing your experience...', icon: Check });
    }

    messages.push({ text: 'Almost ready...', icon: Check });

    return messages;
  };

  const messages = getPersonalizedMessages();

  useEffect(() => {
    const intervals = [0, 400, 800, 1200];

    intervals.forEach((delay, index) => {
      setTimeout(() => {
        if (index < messages.length) {
          setMessageIndex(index);
          if (index > 0) {
            setCompletedSteps((prev) => [...prev, index - 1]);
          }
        }
      }, delay);
    });
  }, []);

  return (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="text-center space-y-8 w-full max-w-md">
        {messages.map((message, index) => {
          const Icon = message.icon;
          const isActive = index === messageIndex;
          const isCompleted = completedSteps.includes(index);
          const isVisible = index <= messageIndex;

          let iconColorClass = 'text-gray-400';
          if (isCompleted) {
            iconColorClass = 'text-green-600';
          } else if (isActive) {
            iconColorClass = 'text-blue-600';
          }

          let textColorClass = 'text-muted-foreground/50';
          if (isCompleted) {
            textColorClass = 'text-muted-foreground';
          } else if (isActive) {
            textColorClass = 'text-foreground';
          }

          let iconElement;
          if (isCompleted) {
            iconElement = <Check className="w-6 h-6" />;
          } else if (isActive) {
            iconElement = <Loader className="w-6 h-6 animate-spin" />;
          } else {
            iconElement = <Icon className="w-6 h-6" />;
          }

          return (
            <div
              key={index}
              className={`flex items-center gap-4 transition-all duration-300 ${
                isVisible
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-4'
              }`}
            >
              <div
                className={`flex-shrink-0 transition-all duration-300 ${iconColorClass}`}
              >
                {iconElement}
              </div>
              <div
                className={`text-left text-lg font-medium transition-all duration-300 ${textColorClass}`}
              >
                {message.text}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const OnboardingSurvey: React.FC<OnboardingSurveyProps> = ({
  onClose,
  onComplete,
}) => {
  const {
    onboardingData,
    setHowDidYouHear,
    setUserType,
    toggleRecordingSource,
    toggleUseCase,
    completeSurvey,
    isObTooltipsVariant,
    isDeferredUpgradeVariant,
  } = useOnboarding();

  const { howDidYouHear, userType, recordingSources, useCases } =
    onboardingData;

  const [currentPage, setCurrentPage] = useState(0);
  const [hasNavigated, setHasNavigated] = useState(false);

  // Determine variant name for tracking
  const getVariantName = () => {
    if (isObTooltipsVariant) return 'tooltips';
    if (isDeferredUpgradeVariant) return 'deferred_upgrade';
    return 'survey_only';
  };
  const variantName = getVariantName();

  useEffect(() => {
    posthog.capture('onboarding_started', {
      variant: variantName,
      onboarding_dismissable: true,
    });
  }, [variantName]);

  const howDidYouHearOptions = [
    'I was desperately searching for this',
    'Someone sent me a ScreenApp link',
    'Saw it on social media',
    'A friend/coworker recommended it',
    'Found it in the App Store',
    'I honestly forgot',
  ];

  const userTypeOptions = [
    'I need to remember what was said',
    'I want to create content faster',
    "I'm drowning in meetings",
    'I need to study more efficiently',
    'I collaborate with my team',
    'Just exploring',
  ];

  const recordingSourcesOptions = [
    'Work meetings & calls',
    'School lectures & study sessions',
    'My ideas & voice notes',
    'Interviews & conversations',
    'Podcasts',
    'YouTube content',
    'Whatever I need',
  ];

  const useCasesOptions = [
    'AI-generated summaries',
    'Searchable transcripts',
    'Automatic note-taking',
    'Chat with my recordings',
    'YouTube video summaries',
    'All of the above',
  ];

  const handleNext = () => {
    if (currentPage === 3) {
      setHasNavigated(true);
      setCurrentPage(4);
      // Auto-advance after loading screen
      setTimeout(() => {
        completeSurvey();
        onComplete();
      }, 1500);
    } else if (currentPage < 4) {
      setHasNavigated(true);
      setCurrentPage(currentPage + 1);
    }
  };

  useEffect(() => {
    const pageNames = [
      'how_did_you_hear',
      'user_type',
      'recording_sources',
      'use_cases',
      'loading',
    ];
    if (currentPage < pageNames.length) {
      posthog.capture('onboarding_page_viewed', {
        page_index: currentPage,
        page_name: pageNames[currentPage],
        variant: variantName,
      });
    }
  }, [currentPage, variantName]);

  const handleHowDidYouHearSelect = (option: string) => {
    setHowDidYouHear(option);
    handleNext();
  };

  const handleUserTypeSelect = (option: string) => {
    setUserType(option);
    handleNext();
  };

  const renderPage = () => {
    switch (currentPage) {
      case 0:
        return (
          <div className="space-y-6">
            <DialogTitle className="text-2xl font-bold text-center">
              How did you hear about ScreenApp?
            </DialogTitle>
            <div className="space-y-3">
              {howDidYouHearOptions.map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => handleHowDidYouHearSelect(option)}
                  className={`w-full px-6 py-4 rounded-full text-left transition-all active:scale-[0.98] ${
                    howDidYouHear === option
                      ? 'bg-blue-600 text-white animate-in zoom-in-95 duration-100'
                      : 'bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700'
                  }`}
                >
                  {option}
                </button>
              ))}
            </div>
            <Button
              onClick={handleNext}
              className="w-full py-6 rounded-full text-base font-semibold"
            >
              Next
            </Button>
          </div>
        );

      case 1:
        return (
          <div className="space-y-6">
            <DialogTitle className="text-2xl font-bold text-center">
              Which best describes you?
            </DialogTitle>
            <div className="space-y-3">
              {userTypeOptions.map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => handleUserTypeSelect(option)}
                  className={`w-full px-6 py-4 rounded-full text-left transition-all active:scale-[0.98] ${
                    userType === option
                      ? 'bg-blue-600 text-white animate-in zoom-in-95 duration-100'
                      : 'bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700'
                  }`}
                >
                  {option}
                </button>
              ))}
            </div>
            <Button
              onClick={handleNext}
              className="w-full py-6 rounded-full text-base font-semibold"
            >
              Next
            </Button>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <DialogTitle className="text-2xl font-bold text-center">
              What will you record most?
            </DialogTitle>
            <div className="space-y-3">
              {recordingSourcesOptions.map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => toggleRecordingSource(option)}
                  className={`w-full px-6 py-4 rounded-full text-left flex items-center gap-3 transition-all active:scale-[0.98] ${
                    recordingSources.has(option)
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700'
                  }`}
                >
                  <div
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-transform ${
                      recordingSources.has(option)
                        ? 'border-white bg-white scale-110'
                        : 'border-gray-400'
                    }`}
                  >
                    {recordingSources.has(option) && (
                      <Check className="w-4 h-4 text-blue-600 animate-in zoom-in-50 duration-100" />
                    )}
                  </div>
                  <span className="flex-1">{option}</span>
                </button>
              ))}
            </div>
            <Button
              onClick={handleNext}
              className="w-full py-6 rounded-full text-base font-semibold"
            >
              Almost done
            </Button>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <DialogTitle className="text-2xl font-bold text-center">
              What would help you most?
            </DialogTitle>
            <p className="text-sm text-muted-foreground text-center -mt-2">
              We&apos;ll personalize your experience based on your selection
            </p>
            <div className="space-y-3">
              {useCasesOptions.map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => toggleUseCase(option)}
                  className={`w-full px-6 py-4 rounded-full text-left flex items-center gap-3 transition-all active:scale-[0.98] ${
                    useCases.has(option)
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700'
                  }`}
                >
                  <div
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-transform ${
                      useCases.has(option)
                        ? 'border-white bg-white scale-110'
                        : 'border-gray-400'
                    }`}
                  >
                    {useCases.has(option) && (
                      <Check className="w-4 h-4 text-blue-600 animate-in zoom-in-50 duration-100" />
                    )}
                  </div>
                  <span className="flex-1">{option}</span>
                </button>
              ))}
            </div>
            <Button
              onClick={handleNext}
              className="w-full py-6 rounded-full text-base font-semibold bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600"
            >
              Let&apos;s go!
            </Button>
          </div>
        );

      case 4:
        return (
          <LoadingScreen
            recordingSources={recordingSources}
            useCases={useCases}
          />
        );

      default:
        return null;
    }
  };

  const handleDialogClose = (open: boolean) => {
    if (!open) {
      posthog.capture('onboarding_skipped', {
        current_page: currentPage,
        variant: variantName,
        how_did_you_hear: howDidYouHear,
        user_type: userType,
        recording_sources: Array.from(recordingSources),
        use_cases: Array.from(useCases),
      });

      // Pass true to indicate survey was skipped - will show 'all' tooltip
      completeSurvey(true);
      onClose();
    }
  };

  return (
    <Dialog open onOpenChange={handleDialogClose}>
      <DialogContent
        className="max-w-lg w-full overflow-hidden p-0 gap-0"
        data-testid="onboarding-survey-modal"
      >
        <div className="h-1 bg-gray-200 dark:bg-gray-800 overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-blue-600 to-blue-500 transition-all duration-300 ease-out"
            style={{
              width: `${((currentPage + 1) / 5) * 100}%`,
            }}
          />
        </div>

        <div className="relative overflow-hidden px-6 pb-6 pt-4">
          <div
            key={currentPage}
            className={
              hasNavigated
                ? 'animate-in slide-in-from-right duration-200 fade-in'
                : ''
            }
          >
            {renderPage()}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OnboardingSurvey;
